/* Copyright (c) 2025 MOCA Systems, Inc.  All rights reserved. */
import { AbstractApplication } from '@appCanvasCommon/core/AbstractApplication';
import { map, takeUntil } from 'rxjs/operators';
import { CanvasAngularLand } from '../../../../app-data/interfaces/canvas-angular-land';
import { LayerPaths } from '@appPlanApplication/LayerPaths';
import { AbstractAreaDrawer } from '@appCanvasCommon/core/AbstractAreaDrawer';
import { EventMode } from '@appCanvasCommon/events/event-mode.enum';
import { combineLatest } from 'rxjs';
import { ViewportChangedEvent } from '@appCanvasCommon/interfaces/camera.interface';
import { DestroyOptions } from '@appCanvasCommon/core/DestroyOptions';
import { IFbdbPullSpace } from '../../../../modules/plan/services/pull-space.service';
import { cssVar } from '@touchplan/css-var';
import { PullColumn } from '@touchplan/common';
import { Spread } from '../../../../utils/Spread';
import { Rectangle } from 'pixi.js-legacy';
import { PullSpaceTimelineDrawer } from '@appPlanApplication/timeline/pull-space/PullSpaceTimelineDrawer';
import { getPullSpaceStart } from '@appPlanApplication/timeline/TimelineHelpers';

const settings = {
	separatorLineColor: cssVar.grey12,
	separatorLineWidth: 1, // in pixels
	spreadPropertyName: `finishX`,
	spreaderColumnWidth: 330, // magic number that came from old code...no clue why 330 was chosen
	spreaderColumnMinWidth: 75, // magic number that came from old AngularJS code...no clue why 75 was chosen.
};
export class PullSpaceDrawer extends AbstractAreaDrawer {
	/**
	 * We store the last Pull Columns as a cache to handle cases where the canvas is forced to redraw
	 * @private
	 */
	private _cachedPullSpace: IFbdbPullSpace | null = null;
	/**
	 * Instance of our Spreader, which is responsible for _spreading_ the columns
	 * @private
	 */
	private _spreader: Spread<PullColumn> = new Spread<PullColumn>('finishX');
	/**
	 * The PullSpaceTimelineDrawer is responsible for drawing all of the timeline details for the pull space.
	 * It is also responsible for handling visibility/
	 * @private
	 */
	private _timelineDrawer: PullSpaceTimelineDrawer = new PullSpaceTimelineDrawer(
		this._canvasAngularLand,
		this.parentApplication
	);

	/**
	 * PullSpaceDrawer handles management of the pull space view.
	 * @param _canvasAngularLand
	 * @param parentApplication
	 */
	constructor(private _canvasAngularLand: CanvasAngularLand, parentApplication: AbstractApplication) {
		super(parentApplication, LayerPaths.BACKGROUND_VISUALS_PULL_SPACE_COLUMNS);
		this._container.eventMode = EventMode.Disabled;
		this.setupSubscribers();
	}
	/**
	 * Draw the Pull Space Visual. This includes:
	 * - Column line separators (entire height of screen)
	 * - Timeline date text labels
	 * - Timeline sprite indicators
	 * @param pullSpace IFbdbPullSpace
	 */
	public draw(pullSpace: IFbdbPullSpace | null = this._cachedPullSpace): void {
		if (!pullSpace || pullSpace.pullFinishX === undefined) {
			this.hide();
			this._timelineDrawer.draw(null);
			return;
		}
		this._cachedPullSpace = pullSpace;
		// We update the container position early because our drawing logic depends an updated local matrix on the parent
		// this is different from other drawers because we are drawing multiple things here (separator lines)
		// and need them to draw relative to the origin of the container.
		this.update();

		// Parent draw clears our graphics drawing data and sets the container to visible
		super.draw();

		const cameraView: Rectangle = this.parentApplication.camera.view;
		const cameraScale: number = this.parentApplication.camera.scale.x;
		const columns: PullColumn[] = pullSpace.columns;

		const spreadedColumns: PullColumn[] = this._spreader.spread(
			columns, // the columns to spread
			-cameraView.x / cameraScale, // the left edge of the drawing area
			Math.ceil(cameraView.width / cameraScale) + settings.spreaderColumnWidth, // total width of the spreader area
			settings.spreaderColumnMinWidth / cameraScale // minimum spacing between spreads
		);

		spreadedColumns.forEach((column: PullColumn) => {
			// We draw relative to the container in screen space. So collect the container position
			// and then subtract that value after converting column world space to the screen
			// this then gives us a relative position from the container origin point.
			const screenX: number = this.parentApplication.camera.realToScreenX(column.finishX) - this._container.x;
			this._graphics.beginFill(settings.separatorLineColor);
			this._graphics.drawRect(screenX, 0, settings.separatorLineWidth, cameraView.height);
			this._graphics.endFill();
		});

		this._timelineDrawer.draw(spreadedColumns);
	}

	/**
	 * Updates the position of the container.
	 * @param _ The time since the last frame, not used.
	 */
	public update(delta?: number): void {
		if (!this._cachedPullSpace) {
			return;
		}
		const data: IFbdbPullSpace = this._cachedPullSpace;
		super.update(
			this.parentApplication.camera.realToScreenX(getPullSpaceStart(data.columns)),
			cssVar.headerHeight_px
		);
		this._timelineDrawer.update(delta);
	}

	/**
	 * Destroy all the things. We need to override the abstract destroy so we can clear up our objects specific to this model
	 * @param options
	 */
	public destroy(options: DestroyOptions = DestroyOptions.includeChildren): void {
		super.destroy(options);
		this._timelineDrawer.destroy(DestroyOptions.sourceOnly);
		this._canvasAngularLand = null;
	}

	/**
	 * Setup all internal subscriptions that causes this drawer to re-draw
	 * @private
	 */
	private setupSubscribers(): void {
		combineLatest([this.parentApplication.camera.changed$, this._canvasAngularLand.pullSpaceService.changed$])
			.pipe(
				takeUntil(this._shutdownSubject$),
				map(([changedEvent, pullSpace]: [ViewportChangedEvent, IFbdbPullSpace]) => pullSpace)
			)
			.subscribe((pullSpace: IFbdbPullSpace) => this.draw(pullSpace));
	}
}
